import { useState, useEffect, useContext, useRef } from "react";
import Select from "react-select";
import { Navbar, Nav } from "react-bootstrap";

import { UserSettingsContext } from "../../contexts/UserSettingsContext";

import {
  Search,
} from "react-feather";

import useSidebar from "../../hooks/useSidebar";

import authenticatedFetch from "../../utils/fetch"
import SearchOption, { SearchShortcutHint } from "../../utils/SearchOption";

import NavbarUser from "./NavbarUser";


const searchSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    paddingLeft: 26,
    paddingRight: 52,
    borderRadius: 999,
    borderColor: state.isFocused ? "#3AAFA9" : "#dee2e6",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(58, 175, 169, .15)" : "none",
    "&:hover": { borderColor: state.isFocused ? "#3AAFA9" : "#ced4da" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  placeholder: (base) => ({ ...base, color: "#6c757d" }),
  menu: (base) => ({
    ...base,
    marginTop: 6,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(0, 0, 0, .12)",
    zIndex: 1050,
  }),
  menuList: (base) => ({ ...base, padding: 6 }),
  option: (base, state) => ({
    ...base,
    borderRadius: 8,
    padding: "8px 10px",
    cursor: "pointer",
    color: "inherit",
    backgroundColor: state.isFocused ? "#DEF2F1" : "transparent",
  }),
  noOptionsMessage: (base) => ({ ...base, color: "#6c757d" }),
};

const ProviderTinSearch = () => {
  const [data, setData] = useState([]);
  const [inputValue, setInputValue] = useState("");

  const { navbarRefresh } = useContext(UserSettingsContext);

  useEffect(() => {
    authenticatedFetch({
      path: "provider/searchlist",
      successHandler: function(result) {
        setData(result);
      }
    });
  }, [navbarRefresh])

  let optionList = [];
  if (data && data.length > 0) {
    optionList = data.map((searchItem) => {
      return (
        {
          value: searchItem.entityType + ":" + searchItem.id,
          id: searchItem.entityType + ":" + searchItem.id,
          entityType: searchItem.entityType,
          name: searchItem.formattedName,
          label: "[" + searchItem.entityTypeLabel + "] " + searchItem.formattedName,
          npi: searchItem.npi,
        }
      )
    });
  }

  const selectRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        selectRef.current && selectRef.current.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="navbar-search">
      <Search className="feather navbar-search-icon" />
      <Select
        ref={selectRef}
        inputId="search-providers-navbar"
        options={optionList}
        value={null}
        placeholder="Search practices and providers..."
        components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null, Option: SearchOption }}
        styles={searchSelectStyles}
        inputValue={inputValue}
        onInputChange={(val, meta) => {
          if (meta.action === "input-change") setInputValue(val);
          if (meta.action === "menu-close" || meta.action === "set-value") setInputValue("");
        }}
        menuIsOpen={inputValue.length > 0}
        noOptionsMessage={() => "No matching practices or providers"}
        onChange={(selected) => {
          if (!selected) return;
          if (selected.id.startsWith("practice"))
          {
            window.location = "/practice/" + selected.id.substring(9);
          }
          else
          {
            window.location = "/provider/" + selected.id.substring(9);
          }
        }}
        filterOption={(option, rawInput) => {
          const queryWords = rawInput.trim().toLowerCase().split(/\s+/).filter(Boolean);
          // Split on whitespace/punctuation so each word (and the NPI) matches on its own
          const labelWords = (option.data.label + " " + (option.data.npi || ""))
            .toLowerCase()
            .split(/[\s,()[\]]+/)
            .filter(Boolean);
          return queryWords.every((queryWord) =>
            labelWords.some((labelWord) => labelWord.includes(queryWord))
          );
        }}
      />
      <SearchShortcutHint />
    </div>
  );
}

// Hide the navbar once scrolled down; it returns near the top of the page (Cmd/Ctrl+K also brings it back)
function useHideOnScroll(hideAfter) {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const onScroll = (e) => {
      if (e.target !== document) return; // ignore scrolling inside inner containers
      const y = document.scrollingElement ? document.scrollingElement.scrollTop : window.scrollY;
      setHidden(y > hideAfter);
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [hideAfter]);

  return hidden;
}

const NavbarComponent = () => {
  const { isOpen, setIsOpen } = useSidebar();

  const { environment, isH3Admin, authorities } = useContext(UserSettingsContext);

  const isDev = (environment === "dev" || environment === "staging")

  const scrolledDown = useHideOnScroll(60);
  // Stay visible while the search or user menu has focus (e.g. after Cmd/Ctrl+K)
  const [hasFocus, setHasFocus] = useState(false);
  const hidden = scrolledDown && !hasFocus;

  return (
    <Navbar
      sticky="top"
      variant="light"
      expand
      className={"navbar-bg" + (hidden ? " navbar-hidden" : "")}
      onFocus={() => setHasFocus(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setHasFocus(false);
      }}
      style={{background : isDev ? "#2196F3" : ""}}>
      <span
        className="sidebar-toggle d-flex"
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        <i className="hamburger align-self-center" />
      </span>

      {/* Could ultimately implment a version of this that allows just provider/practice search, based on permissions */}
      {(isH3Admin || (
        (authorities.includes("PROVIDER_BASIC_VIEW") || authorities.includes("PROVIDER_BASIC_EDIT")) &&
        (authorities.includes("PRACTICE_BASIC_VIEW") || authorities.includes("PRACTICE_BASIC_EDIT"))
        )) ?
      <div className="d-none d-sm-block navbar-search-wrap">
        <ProviderTinSearch />
      </div>
      : <></>
      }
      
      {isDev?
      <h4>&nbsp;&nbsp;{environment}&nbsp;&nbsp;</h4>
      : <></>
      }
      <Navbar.Collapse>
        <Nav
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
          }}
        >
          <NavbarUser />
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  );
};

export default NavbarComponent;
